import React from 'react';
import {useTranslation} from 'react-i18next';

/**
 * UN GIOCO NELLA LOBBY: la copertina, il fornitore in piccolo, il nome.
 *
 * Senza copertina dal fornitore si vede il nome sul colore della skin, non
 * un'immagine inventata. Il cuore (preferiti) e «Prova» compaiono solo se il
 * modulo li tiene accesi; «Prova» solo sui giochi per cui il fornitore la
 * offre.
 */

/* LA COPERTINA CHE ARRIVA. Finche' l'immagine non c'e' si vede un riquadro
   che luccica (come fanno tutte le lobby), poi l'immagine entra sfumando:
   mai il fondo colorato da solo, che sembrava un gioco senza disegno.
   Se il server delle immagini non risponde (capita, con centinaia di
   copertine chieste insieme) si riprova da soli, due volte, prima di
   arrendersi e mostrare il nome: prima restava il colore finche' non si
   ricaricava la pagina. */
const RIPROVE = 2;
const ATTESA_IMMAGINE = 10000;

/* LA COPERTINA NON SI TAGLIA TROPPO. Le copertine dei fornitori hanno forme
   di ogni tipo (quadrate, 4:3, larghe quasi il doppio dell'altezza, alte),
   il riquadro invece ha la sua forma fissa: riempito a forza, di una
   copertina larga restava solo il centro e i titoli uscivano mozzati
   («ES of OLYM»). Adesso si taglia al piu' il 15% dell'immagine; quello che
   avanza del riquadro lo riempie la stessa copertina sfocata e scurita
   dietro. L'immagine non si stira mai. Quelle quasi della forma giusta
   restano a riempire il riquadro come prima, senza sfondo. */
const TAGLIO_MASSIMO = 0.15;
const ZOOM_MASSIMO = 1 / (1 - TAGLIO_MASSIMO);

/** Quanto ingrandire l'immagine intera (contain) per tagliarla al massimo del 15%; null se puo' riempire da sola. */
export function zoomCopertina(larghezza, altezza, riquadroL, riquadroA) {
  if (!larghezza || !altezza || !riquadroL || !riquadroA) return null;
  const forma = larghezza / altezza;
  const riquadro = riquadroL / riquadroA;
  const pieno = Math.max(forma / riquadro, riquadro / forma);
  return pieno > ZOOM_MASSIMO + 0.01 ? ZOOM_MASSIMO : null;
}

/* Il riquadro cambia forma col cambio di schermo (3:4 sul telefono, quadrato
   dal computer): un osservatore solo per tutte le copertine rifa' il conto. */
const alCambio = new WeakMap();
let osservatore = null;
function osserva(el, fn) {
  if (typeof ResizeObserver === 'undefined') return () => {};
  if (!osservatore) osservatore = new ResizeObserver((voci) => voci.forEach((v) => { const f = alCambio.get(v.target); if (f) f(); }));
  alCambio.set(el, fn);
  osservatore.observe(el);
  return () => { alCambio.delete(el); osservatore.unobserve(el); };
}

export function ImmagineCopertina({indirizzo, onFallita}) {
  const [stato, setStato] = React.useState('arriva');   // arriva | pronta
  const [giro, setGiro] = React.useState(0);
  const [zoom, setZoom] = React.useState(null);
  /* Piu' stretta del riquadro (fasce ai lati) o piu' larga (fasce sopra e sotto). */
  const [stretta, setStretta] = React.useState(false);
  const img = React.useRef(null);
  React.useEffect(() => { setStato('arriva'); setGiro(0); setZoom(null); }, [indirizzo]);
  React.useEffect(() => {
    const el = img.current;
    const riquadro = el && el.parentElement;
    if (stato !== 'pronta' || !riquadro) return undefined;
    const conta = () => {
      const r = riquadro.getBoundingClientRect();
      setZoom(zoomCopertina(el.naturalWidth, el.naturalHeight, r.width, r.height));
      setStretta(r.height > 0 && el.naturalWidth / el.naturalHeight < r.width / r.height);
    };
    conta();
    return osserva(riquadro, conta);
  }, [stato]);
  /* Un'immagine gia' nella cache del browser puo' essere pronta prima che
     React la guardi: si controlla a mano. Se invece dopo un po' non e'
     arrivata ne' ha dato errore, si richiede. */
  React.useEffect(() => {
    const el = img.current;
    if (el && el.complete && el.naturalWidth > 0) { setStato('pronta'); return undefined; }
    if (stato === 'pronta') return undefined;
    const orologio = setTimeout(() => {
      const ora = img.current;
      if (!ora) return;
      if (ora.complete && ora.naturalWidth > 0) { setStato('pronta'); return; }
      /* Pigra e fuori dallo schermo non e' bloccata: aspetta solo di servire. */
      const r = ora.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) return;
      if (giro < RIPROVE) setGiro(giro + 1);
    }, ATTESA_IMMAGINE);
    return () => clearTimeout(orologio);
  }, [stato, giro, indirizzo]);
  const sorgente = giro ? indirizzo + (indirizzo.indexOf('?') === -1 ? '?' : '&') + 'r=' + giro : indirizzo;
  return (
    <>
      {stato !== 'pronta' && <span className={'cas-luccica'} aria-hidden={'true'}/>}
      {zoom && <span className={'cas-sfondo' + (stretta ? ' stretta' : '')} aria-hidden={'true'} style={{'--sfondo': `url("${sorgente.replace(/"/g, '%22')}")`}}/>}
      <img ref={img} key={sorgente} src={sorgente} alt={''} loading={'lazy'} decoding={'async'}
        className={(stato === 'pronta' ? 'pronta' : '') + (zoom ? ' intera' : '')} style={zoom ? {'--zoom': zoom} : undefined}
        onLoad={() => setStato('pronta')}
        onError={() => {
          if (giro < RIPROVE) setTimeout(() => setGiro((g) => g + 1), 600 * (giro + 1));
          else onFallita();
        }}/>
    </>
  );
}

/* Il colore della copertina senza immagine (lo stile Nova lo usa): lo stesso
   gioco ha sempre lo stesso colore, giochi diversi colori diversi. Non dice
   niente del gioco: e' solo il fondo su cui sta il nome. */
const TONI = [265, 330, 25, 190, 140, 45, 210, 300, 5, 170];
export function tono(id) {
  let h = 0;
  String(id).split('').forEach((c) => { h = (h * 31 + c.charCodeAt(0)) % 997; });
  return TONI[h % TONI.length];
}

export default function Copertina({gioco, preferito, conPreferiti, conProva, onApri, onProva, onPreferito}) {
  const {t} = useTranslation();
  const [fallita, setFallita] = React.useState(false);
  /* Se la nostra copertina ridotta (/copertine/) non arriva, si prova quella
     del fornitore prima di arrendersi al nome. */
  const [dalFornitore, setDalFornitore] = React.useState(false);
  React.useEffect(() => { setFallita(false); setDalFornitore(false); }, [gioco.copertina]);
  const conOriginale = !!gioco.originale && gioco.originale !== gioco.copertina;
  const indirizzo = dalFornitore ? gioco.originale : gioco.copertina;
  const conImmagine = !!indirizzo && !fallita;
  return (
    <div className={'cas-gioco'}>
      <button type={'button'} className={'cas-copertina' + (conImmagine ? ' con-immagine' : ' senza')} style={{'--h': tono(gioco.id)}} onClick={() => onApri(gioco)}
        aria-label={t('Gioca a {{nome}}', {nome: gioco.nome})}>
        {conImmagine
          ? <ImmagineCopertina indirizzo={indirizzo}
              onFallita={() => (conOriginale && !dalFornitore ? setDalFornitore(true) : setFallita(true))}/>
          : <span className={'cas-iniziali'}>{gioco.nome}</span>}
        {gioco.nuovo && <span className={'cas-nuovo'}>{t('Nuovo')}</span>}
        <span className={'cas-gioca'} aria-hidden={'true'}>▶</span>
      </button>
      {conPreferiti && (
        <button type={'button'} className={'cas-cuore' + (preferito ? ' acceso' : '')} onClick={() => onPreferito(gioco.id)}
          aria-pressed={preferito} aria-label={preferito ? t('Togli dai preferiti') : t('Aggiungi ai preferiti')}>♥</button>
      )}
      <div className={'cas-didascalia'}>
        <small>{gioco.fornitore}</small>
        <span className={'cas-nome'}>{gioco.nome}</span>
      </div>
      {conProva && gioco.prova && (
        <button type={'button'} className={'cas-prova'} onClick={() => onProva(gioco)}>{t('Prova')}</button>
      )}
    </div>
  );
}
